import type { Cart, CartCountItem, CartItem, FreeDeliveryTerm } from '~/types/Cart';
import { cloneStoreState } from '../utils/storeState.ts';
import {
  CartSyncQueue,
  type CartSyncStatus,
  getChangedCartItemIds,
  getCartCountItems,
  normalizeCartResponse,
} from '../utils/cartSyncQueue.ts';
import { hasConfirmedCartIncrease, notifyQualifiedCartAddConfirmed } from '../utils/qualifiedAddToCart.ts';

export interface CartStore {
  cart: Cart;
  cartLoadError: string | null;
  cartLoadStatus: 'idle' | 'loading' | 'loaded' | 'failed';
  cartSyncError: string | null;
  cartSyncStatus: CartSyncStatus;
  selectedCartItems: CartItem[];
  countedCartItems: CartCountItem[];
  isCartEntrySyncPending: boolean;
  isCartInitialized: boolean;
  isCartLoaded: boolean;
}

interface CartSyncRuntime {
  cartLoadPromise?: Promise<boolean>;
  queue: CartSyncQueue;
}

const cartSyncRuntimes = new WeakMap<object, CartSyncRuntime>();

export const useCartStore = createCartStore({
  cart: {
    calculate: {
      discount_data: [],
      discounts: [],
      loyalty: {
        action: 'income',
        available_to_withdraw: 0,
        income: 0,
      },
      offer_price_and_discount: [],
      price_without_discounts: 0,
      promo_code: undefined,
      total: 0,
      totalDiscounts: {},
    },
    cart_id: 0,
    items: [],
    restrictions: { required_gism: false },
    status: '',
  },
  cartLoadError: null,
  cartLoadStatus: 'idle',
  cartSyncError: null,
  cartSyncStatus: 'idle',
  selectedCartItems: [],
  countedCartItems: [],
  isCartEntrySyncPending: false,
  isCartInitialized: false,
  isCartLoaded: false,
});

async function fetchCartClient() {
  const data = await useCustomFetchClient<Cart>('/cart', {}, '2', true, true);
  const cart = normalizeCartResponse(data);

  if (!cart) throw new Error('Cart API returned an invalid response');

  return cart;
}

function getCartSyncRuntime(store: any): CartSyncRuntime {
  const runtimeKey = store.$pinia ?? store;
  const existingRuntime = cartSyncRuntimes.get(runtimeKey);

  if (existingRuntime) return existingRuntime;

  const runtime: CartSyncRuntime = {
    queue: new CartSyncQueue({
      async confirm() {
        return await fetchCartClient();
      },
      initialConfirmedItems: store.isCartInitialized ? getCartCountItems(store.cart) : [],
      onConfirmed(cart) {
        store.setCartStoreState(cart);
      },
      onDesiredChange(items) {
        store.countedCartItems = items;
      },
      onStatusChange(status, error) {
        store.cartSyncStatus = status;
        store.cartSyncError = status === 'failed' ? 'Не удалось сохранить изменения корзины' : null;

        if (!import.meta.client) return;

        const { dismissCartSyncError, showCartSyncError } = useCartSyncErrorToast();

        if (status === 'failed') showCartSyncError(() => runtime.queue.retry());
        if (status === 'idle') dismissCartSyncError();

        if (error && process.env.NODE_ENV !== 'production') console.error(error);
      },
      onSynchronized({ confirmedItems, previousConfirmedItems, submittedItems }) {
        try {
          if (!hasConfirmedCartIncrease(previousConfirmedItems, submittedItems, confirmedItems)) return;

          notifyQualifiedCartAddConfirmed();
        } catch {
          // Analytics must never affect confirmed cart synchronization.
        }
      },
      async send(items) {
        await useCustomFetchClient(
          '/cart',
          {
            body: { data: items, requestId: getRandomString(10) },
            method: 'POST',
          },
          '2',
          true,
          true,
        );
      },
    }),
  };

  cartSyncRuntimes.set(runtimeKey, runtime);

  return runtime;
}

export function createCartStore(initialState: CartStore) {
  return defineStore('cart', {
    state: () => cloneStoreState(initialState),

    getters: {
      pendingCartItemIds(): number[] {
        return getChangedCartItemIds(getCartCountItems(this.cart), this.countedCartItems);
      },

      prospectiveCartState(): CartCountItem[] {
        return this.countedCartItems;
      },
    },

    actions: {
      async getSharedOffers(ids: string): Promise<CartItem['data'][]> {
        try {
          return (await useCustomFetchClient<CartItem['data'][]>('/cart/offers', { query: { ids } }, '2', true)) ?? [];
        } catch (error) {
          if (import.meta.dev) console.error(error);
          return [];
        }
      },

      setCartStoreState(data: Cart) {
        this.cart = data;
        this.countedCartItems = getCartCountItems(data);
        this.selectedCartItems = data.items.filter((item: CartItem) => item.is_selected);
        this.cartLoadError = null;
        this.cartLoadStatus = 'loaded';
        this.isCartEntrySyncPending = false;
        this.isCartInitialized = true;
        this.isCartLoaded = true;
      },

      restoreConfirmedSelectedCartItems() {
        this.selectedCartItems = this.cart.items.filter((item: CartItem) => item.is_selected);
      },

      applyCartPublication(data: unknown) {
        const cart = normalizeCartResponse(data);

        if (!cart) return false;

        return getCartSyncRuntime(this).queue.acceptConfirmed(cart);
      },

      async getCart() {
        const runtime = getCartSyncRuntime(this);

        if (runtime.cartLoadPromise) return await runtime.cartLoadPromise;

        runtime.cartLoadPromise = (async () => {
          const isInitialLoad = !this.isCartInitialized;

          this.cartLoadError = null;
          this.cartLoadStatus = 'loading';

          if (isInitialLoad) this.isCartLoaded = false;

          try {
            const data = import.meta.client
              ? await fetchCartClient()
              : (await useCustomFetch<Cart>('/cart', {}, '2')).data.value;

            const cart = normalizeCartResponse(data);

            if (!cart) throw new Error('Cart API returned an invalid response');

            const wasAccepted = runtime.queue.acceptConfirmed(cart);

            if (wasAccepted) {
              const unavailableItems = this.cart.items.filter((item) => item.available_count === 0);

              if (unavailableItems.length) {
                void this.changeSelectedCartItems(
                  unavailableItems.map((item) => item.id),
                  true,
                );
              }
            }

            if (!wasAccepted) this.cartLoadStatus = this.isCartInitialized ? 'loaded' : 'loading';

            return wasAccepted;
          } catch (error) {
            this.cartLoadError = 'Не удалось загрузить корзину';
            this.cartLoadStatus = 'failed';

            if (error && process.env.NODE_ENV !== 'production') console.error(error);

            return false;
          } finally {
            if (this.isCartInitialized) this.isCartLoaded = true;
          }
        })().finally(() => {
          runtime.cartLoadPromise = undefined;
        });

        return await runtime.cartLoadPromise;
      },

      async changeSelectedCartItems(offerIds: number[], isSelected: boolean) {
        if (!(await this.flushCartChanges())) {
          this.restoreConfirmedSelectedCartItems();

          return false;
        }

        try {
          const data = await useCustomFetchClient<Cart>(
            '/cart/items',
            {
              body: {
                offer_ids: offerIds,
                action: isSelected ? 'unselect' : 'select',
              },
              method: 'POST',
            },
            '2',
            true,
            true,
          );

          const cart = normalizeCartResponse(data);

          const wasAccepted = Boolean(cart && getCartSyncRuntime(this).queue.acceptConfirmed(cart));

          if (!wasAccepted) this.restoreConfirmedSelectedCartItems();

          return wasAccepted;
        } catch (error) {
          this.restoreConfirmedSelectedCartItems();

          if (error && process.env.NODE_ENV !== 'production') console.error(error);

          return false;
        }
      },

      async changeLoyaltyAction(action: string) {
        if (!(await this.flushCartChanges())) return false;

        try {
          const data = await useCustomFetchClient<Cart>(
            '/cart/bonus',
            {
              body: { action },
              method: 'POST',
            },
            '2',
            true,
            true,
          );

          const cart = normalizeCartResponse(data);

          if (cart) getCartSyncRuntime(this).queue.acceptConfirmed(cart);

          return Boolean(cart);
        } catch (error) {
          if (error && process.env.NODE_ENV !== 'production') console.error(error);

          return false;
        }
      },

      changeCartState(newCartState: CartCountItem[], showCartEntryLoader = true) {
        if (showCartEntryLoader) this.isCartEntrySyncPending = true;

        getCartSyncRuntime(this).queue.enqueue(newCartState);

        return true;
      },

      async flushCartChanges() {
        return await getCartSyncRuntime(this).queue.flush();
      },

      async retryCartSync() {
        return await getCartSyncRuntime(this).queue.retry();
      },

      async addToCart(offerId: number) {
        if (!this.isCartInitialized && !(await this.getCart())) return false;

        const existingItem = this.prospectiveCartState.find((item) => item.id === offerId);
        const newCartState = existingItem
          ? this.prospectiveCartState.map((item) => (item.id === offerId ? { ...item, count: item.count + 1 } : item))
          : [...this.prospectiveCartState, { id: offerId, count: 1 }];

        return this.changeCartState(newCartState);
      },

      removeFromCart(offerIds: number[], showCartEntryLoader = true) {
        const filteredItems = this.prospectiveCartState.filter((item) => !offerIds.includes(item.id));

        return this.changeCartState(filteredItems, showCartEntryLoader);
      },

      increment(offerId: number, showCartEntryLoader = true) {
        const item = this.prospectiveCartState.find((cartItem) => cartItem.id === offerId);

        if (!item) return false;

        return this.changeCartState(
          this.prospectiveCartState.map((cartItem) =>
            cartItem.id === offerId ? { ...cartItem, count: cartItem.count + 1 } : cartItem,
          ),
          showCartEntryLoader,
        );
      },

      decrement(offerId: number, showCartEntryLoader = true) {
        const item = this.prospectiveCartState.find((cartItem) => cartItem.id === offerId);

        if (!item) return false;
        if (item.count === 1) return this.removeFromCart([offerId], showCartEntryLoader);

        return this.changeCartState(
          this.prospectiveCartState.map((cartItem) =>
            cartItem.id === offerId ? { ...cartItem, count: cartItem.count - 1 } : cartItem,
          ),
          showCartEntryLoader,
        );
      },

      async applyPromoCode(code: string) {
        if (!(await this.flushCartChanges())) return false;

        try {
          const data = await useCustomFetchClient<Cart>(
            '/promo/apply',
            { method: 'POST', body: { code } },
            '1',
            true,
            true,
          );

          const cart = normalizeCartResponse(data);

          if (cart) getCartSyncRuntime(this).queue.acceptConfirmed(cart);

          return Boolean(cart);
        } catch (error) {
          if (error && process.env.NODE_ENV !== 'production') console.error(error);

          return false;
        }
      },

      async removePromoCode() {
        if (!(await this.flushCartChanges())) return false;

        try {
          const data = await useCustomFetchClient<Cart>('/promo/delete', { method: 'DELETE' }, '1', true, true);

          const cart = normalizeCartResponse(data);

          if (cart) getCartSyncRuntime(this).queue.acceptConfirmed(cart);

          return Boolean(cart);
        } catch (error) {
          if (error && process.env.NODE_ENV !== 'production') console.error(error);

          return false;
        }
      },

      async getFreeDeliveryTerms() {
        try {
          const { data } = await useCustomFetch<FreeDeliveryTerm[]>('/free-delivery');

          return data.value ?? [];
        } catch (error) {
          if (error && process.env.NODE_ENV !== 'production') console.error(error);

          return [];
        }
      },
    },
  });
}
